import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { ApplicationModel, AuthorizedPersonnelModel, BeneficialOwnerModel, PersonnelModel, UsPersonModel } from '../model';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthorizedPersonnelOption, BeneficialOwnerOption, UsPersonOption } from '../type';
import { PERSONNEL_OTHER } from '../const/app.const';
import { FinancialRequiredStore } from './financial-required.store';
import { ApplicationHelperService } from '../service';
import { Method } from '@core/data-access';

type State = { state: 'idle' | 'invalid' } | { state: 'valid'; data: ApplicationModel };

const initialState: State = { state: 'idle' };

@Injectable({ providedIn: 'root' })
export class ApplicationStore extends ComponentStore<State> {
  private readonly financialRequiredStore = inject(FinancialRequiredStore);
  readonly selectData$ = this.select(s => (s.state === 'valid' ? s : null));
  readonly application$ = this.select(s => (s.state === 'valid' ? s.data : null));
  readonly soleProprietorship$ = new BehaviorSubject<boolean>(false);
  readonly setApplication = this.updater<ApplicationModel>((_, data) => {
    const application = ApplicationModel.fromJson(data);

    this.soleProprietorship$.next(application.businessEntity?.businessClassification === 'SOLE_PROPRIETORSHIP');
    this.financialRequiredStore.setFormDataBasedOnApplication(application);

    return { state: 'valid', data: application };
  });
  readonly appUsPersonOptionsMap$ = new BehaviorSubject<Map<string, UsPersonOption>>(new Map());
  readonly appPersonnelOptionsMap$ = new BehaviorSubject<Map<string, AuthorizedPersonnelOption>>(new Map());
  readonly appBeneficialOwnerOptionsMap$ = new BehaviorSubject<Map<string, BeneficialOwnerOption>>(new Map());
  readonly secretarySubmit$ = new Subject<void>();
  readonly partnershipSubmit$ = new Subject<void>();
  readonly personnelDeclarationSubmit$ = new Subject<void>();
  readonly proprietorSubmit$ = new Subject<void>();

  constructor() {
    super(initialState);
  }

  initAppPersonnelOptionsMap(args: { application: ApplicationModel; personnels: PersonnelModel[] }): void {
    const options = args.personnels.map<AuthorizedPersonnelOption>(p => {
      const personnel = AuthorizedPersonnelModel.fromJson(p);
      if (ApplicationHelperService.isPersonnelKeyman(personnel)) {
        personnel.preferredContactTime = args.application.preferredContactTime;
      }

      return { label: p.name, value: p.metadata?.uuid || null, disabled: false, personnel };
    });

    options.push({ label: 'Other', value: PERSONNEL_OTHER, disabled: false, personnel: null });

    const map = new Map(options.map(o => [o.value, o])); // unique by value

    this.appPersonnelOptionsMap$.next(map);
  }

  updateAppPersonnelOptionsMap(args: { personnels: AuthorizedPersonnelModel[] }): void {
    const currentPersonnels = this.appPersonnelOptionsMap$.getValue();
    args.personnels.forEach(personnel => {
      const option = currentPersonnels.get(personnel.uuid || '');
      if (option && personnel.uuid !== PERSONNEL_OTHER) {
        option.personnel = personnel;
        currentPersonnels.set(personnel.metadata?.uuid, option);
      }
    });
    this.appPersonnelOptionsMap$.next(currentPersonnels);
  }

  initAppUsPersonOptionsMap(usPersons: UsPersonModel[]): void {
    const options = usPersons.map<UsPersonOption>(us => {
      return { label: us.name, value: us.uuid || null, disabled: false, usPerson: us };
    });

    options.push({ label: 'Other', value: PERSONNEL_OTHER, disabled: false, usPerson: null });

    const map = new Map(options.map(o => [o.value, o])); // unique by value

    this.appUsPersonOptionsMap$.next(map);
  }

  updateAppUsPersonOptionsMap(args: { usPersons: UsPersonModel[] }): void {
    const currentUsPersonsOptionMap = this.appUsPersonOptionsMap$.getValue();
    args.usPersons.forEach(usPerson => {
      const option = currentUsPersonsOptionMap.get(usPerson.uuid || '');
      if (option) {
        option.usPerson = usPerson;
        currentUsPersonsOptionMap.set(usPerson.metadata?.uuid, option);
      }
    });
    this.appUsPersonOptionsMap$.next(currentUsPersonsOptionMap);
  }

  initAppBeneficialOwnerOptionsMap(beneficialOwners: BeneficialOwnerModel[]): void {
    const options = beneficialOwners
      .filter(bo => bo.method === Method.manual)
      .map<BeneficialOwnerOption>(bo => {
        return { label: bo.name, value: bo.personnel_uuid || null, disabled: false, beneficialOwner: bo };
      });

    options.push({ label: 'Other', value: PERSONNEL_OTHER, disabled: false, beneficialOwner: null });

    const map = new Map(options.map(o => [o.value, o])); // unique by value

    this.appBeneficialOwnerOptionsMap$.next(map);
  }

  updateAppBeneficialOwnerOptionsMap(args: { beneficialOwners: BeneficialOwnerModel[] }): void {
    const currentBOOptionMap = this.appBeneficialOwnerOptionsMap$.getValue();
    args.beneficialOwners.forEach(beneficialOwner => {
      const option = currentBOOptionMap.get(beneficialOwner.personnel_uuid || '');
      if (option && beneficialOwner.personnel_uuid !== PERSONNEL_OTHER) {
        option.beneficialOwner = beneficialOwner;
        currentBOOptionMap.set(beneficialOwner.metadata?.uuid, option);
      }
    });
    this.appBeneficialOwnerOptionsMap$.next(currentBOOptionMap);
  }
}
